import { useState } from "react";
import {
  Download,
  Calendar,
  Users,
  Coins,
  Clock,
  ShieldCheck,
  Search,
  Filter,
  ArrowDownRight,
  Sparkles,
  ChevronDown,
  RefreshCw,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/utils";
import { openPayrollPdf } from "@/lib/payroll-pdf";

interface PayrollTabProps {
  payrollSummary: any;
  downloadExcel: () => void;
  exportAttendance: any;
  idToken?: string | null;
}

export function PayrollTab({
  payrollSummary: initialSummary,
  downloadExcel,
  exportAttendance,
  idToken,
}: PayrollTabProps) {
  const [selectedPeriodIndex, setSelectedPeriodIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const recentPeriods = initialSummary.data?.recentPeriods ?? [];
  const selectedPeriod = recentPeriods[selectedPeriodIndex];

  // Fetch summary for selected bi-monthly period
  const activeSummaryQuery = trpc.payroll.summary.useQuery(
    {
      idToken: idToken || "",
      startDate: selectedPeriod?.startDate,
      endDate: selectedPeriod?.endDate,
    },
    {
      enabled: Boolean(idToken),
      initialData: selectedPeriodIndex === 0 ? initialSummary.data : undefined,
    }
  );

  const summaryData = activeSummaryQuery.data ?? initialSummary.data;
  if (!summaryData && (activeSummaryQuery.isLoading || initialSummary.isLoading)) {
    return (
      <Card className="glass-panel rounded-3xl p-8 text-center">
        <RefreshCw className="mx-auto mb-3 animate-spin text-[#1b6b44]" size={28} />
        <p className="font-black text-slate-800">กำลังคำนวณข้อมูลเงินเดือน…</p>
      </Card>
    );
  }
  if (!summaryData && (activeSummaryQuery.error || initialSummary.error)) {
    return (
      <Card className="glass-panel rounded-3xl border-rose-200 bg-rose-50/90 p-6 text-center">
        <p className="font-black text-rose-800">โหลดข้อมูลเงินเดือนไม่สำเร็จ</p>
        <p className="mt-1 text-xs text-rose-600">
          {(activeSummaryQuery.error || initialSummary.error)?.message}
        </p>
      </Card>
    );
  }
  if (!summaryData) return null;

  const { totals, employees, startDate, endDate, periodLabel } = summaryData;

  // Filter employees by search query
  const filteredEmployees = employees.filter((item: any) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return item.displayName.toLowerCase().includes(q);
  });

  // Export full detailed payroll sheet
  const handleExportPayrollExcel = () => {
    const rows = employees.map((emp: any, index: number) => ({
      ลำดับ: index + 1,
      ชื่อพนักงาน: emp.displayName,
      ค่าแรงรายวัน: emp.payroll.dailyRate,
      วันทำงานจริง: emp.payroll.completeDays,
      วันมาสาย: emp.payroll.lateDays,
      "ชม. OT": emp.payroll.otHours,
      ค่าแรงฐาน: emp.payroll.basePay,
      "ค่าล่วงเวลา (OT)": emp.payroll.otPay,
      ค่าอื่นๆ: emp.payroll.otherAllowances,
      "รวมรายได้ (Gross)": emp.payroll.estimatedGrossPay,
      หักเบิกเงินล่วงหน้า: emp.payroll.advanceDeduction,
      "เงินได้สุทธิ (Net Pay)": emp.payroll.netPay,
    }));

    downloadCsv(rows, `payroll-${startDate}-to-${endDate}.csv`);
    toast.success("ส่งออกไฟล์สรุปเงินเดือน CSV สำหรับ Excel เรียบร้อยแล้ว");
  };

  return (
    <div className="space-y-4">
      {/* Header Card & Period Switcher */}
      <Card className="glass-panel overflow-hidden rounded-3xl border border-[#e5ede7] shadow-lg">
        <div className="h-1.5 bg-gradient-to-r from-[#1b6b44] via-[#248a58] to-[#6ee7b7]" />

        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-black uppercase tracking-wider text-[#1b6b44]">
                  รอบเงินเดือนตัดทุกวันที่ 10 และ 25
                </span>
              </div>
              <CardTitle className="text-xl sm:text-2xl font-black text-slate-900">
                สรุปค่าแรง & คำนวณเงินเดือนสุทธิ
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm text-slate-500 font-medium">
                รอบคำนวณ: <span className="font-bold text-[#1b6b44]">{periodLabel}</span> ({startDate} ถึง {endDate})
              </CardDescription>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => openPayrollPdf({ periodLabel, startDate, endDate, employees, title: "ใบสรุปค่าแรงและ OT ภาพรวม" })}
                className="rounded-full border-emerald-200 text-emerald-800 text-xs font-bold"
              >
                <FileText size={13} className="mr-1.5" /> PDF ภาพรวม
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPayrollExcel}
                className="rounded-full border-[#cbe4d2] text-[#1b6b44] hover:bg-[#ebf6ee] text-xs font-bold shadow-xs"
              >
                <Download size={13} className="mr-1.5" />
                ส่งออก Excel สรุปค่าแรง
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => activeSummaryQuery.refetch()}
                className="rounded-full border-slate-200 text-slate-600 hover:bg-slate-100 text-xs font-bold"
              >
                <RefreshCw size={13} className={activeSummaryQuery.isFetching ? "animate-spin" : ""} />
              </Button>
            </div>
          </div>

          {/* Period Selector Tabs */}
          {recentPeriods.length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-1.5 overflow-x-auto pb-1">
              <span className="text-xs font-bold text-slate-500 shrink-0 mr-1 flex items-center gap-1">
                <Calendar size={13} /> เลือกรอบ:
              </span>
              {recentPeriods.map((period: any, idx: number) => (
                <button
                  key={`${period.startDate}_${period.endDate}`}
                  onClick={() => setSelectedPeriodIndex(idx)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition whitespace-nowrap ${
                    selectedPeriodIndex === idx
                      ? "bg-[#1b6b44] text-white shadow-xs"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200/70"
                  }`}
                >
                  {period.label}
                </button>
              ))}
            </div>
          )}
        </CardHeader>

        <CardContent>
          {/* KPI Metrics Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mb-4">
            <div className="rounded-2xl bg-slate-50 border border-slate-200/80 p-3">
              <p className="text-[11px] font-bold text-slate-500">พนักงานในระบบ</p>
              <p className="text-lg sm:text-xl font-black font-numeric text-slate-900 mt-0.5">
                {totals.employees} <span className="text-xs font-medium text-slate-400">คน</span>
              </p>
            </div>

            <div className="rounded-2xl bg-slate-50 border border-slate-200/80 p-3">
              <p className="text-[11px] font-bold text-slate-500">ค่าแรงฐานรวม</p>
              <p className="text-lg sm:text-xl font-black font-numeric text-slate-900 mt-0.5">
                ฿{totals.basePay.toLocaleString()}
              </p>
            </div>

            <div className="rounded-2xl bg-amber-50/80 border border-amber-200/80 p-3">
              <p className="text-[11px] font-bold text-amber-800">ค่าล่วงเวลา (OT)</p>
              <p className="text-lg sm:text-xl font-black font-numeric text-amber-700 mt-0.5">
                ฿{totals.otPay.toLocaleString()}
              </p>
            </div>

            <div className="rounded-2xl bg-teal-50/80 border border-teal-200/80 p-3">
              <p className="text-[11px] font-bold text-teal-800">ค่าอื่นๆ รวม</p>
              <p className="text-lg sm:text-xl font-black font-numeric text-teal-700 mt-0.5">
                ฿{totals.otherAllowances.toLocaleString()}
              </p>
            </div>

            <div className="rounded-2xl bg-rose-50/80 border border-rose-200/80 p-3">
              <p className="text-[11px] font-bold text-rose-800">หักเบิกเงินล่วงหน้า</p>
              <p className="text-lg sm:text-xl font-black font-numeric text-rose-700 mt-0.5">
                -฿{totals.totalAdvanceDeductions.toLocaleString()}
              </p>
            </div>

            {/* Total Net Pay Highlight */}
            <div className="rounded-2xl bg-[#ebf6ee] border border-[#cbe4d2] p-3 shadow-xs">
              <p className="text-[11px] font-extrabold text-[#1b6b44]">ยอดจ่ายสุทธิรวม</p>
              <p className="text-lg sm:text-xl font-black font-numeric text-[#134e31] mt-0.5">
                ฿{totals.totalNetPay.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Policy Banner */}
          <div className="rounded-2xl bg-emerald-50/70 border border-emerald-200/70 p-3 text-xs text-emerald-950 flex items-start gap-2.5">
            <ShieldCheck size={18} className="text-[#1b6b44] shrink-0 mt-0.5" />
            <p className="leading-relaxed">
              <strong>เกณฑ์การคำนวณรอบเงินเดือน:</strong> เวลางาน 8 ชม. (หักพัก 1 ชม.) · เกิน 8 ชม. 30 นาที ปัดขึ้นเป็น OT (คิดค่าแรงรายวัน/8 × 1.5) · ตัดรอบทุกวันที่ 10 และ 25 · หักยอดเบิกเงินล่วงหน้าที่ได้รับการอนุมัติในรอบบิล
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Employee Breakdown Table / Cards */}
      <Card className="glass-panel overflow-hidden rounded-3xl border border-[#e5ede7] shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="text-base font-black text-slate-900">
                รายชื่อและเงินเดือนรายบุคคล ({filteredEmployees.length} คน)
              </CardTitle>
              <CardDescription className="text-xs text-slate-500 font-medium">
                คำนวณตามจำนวนวันทำงานจริงและชั่วโมง OT ในรอบปัจจุบัน
              </CardDescription>
            </div>

            <div className="relative w-full sm:w-64">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="ค้นหาชื่อพนักงาน..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 text-xs h-9 rounded-xl border-slate-200 bg-white"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-3 sm:px-6 pb-6">
          <div className="space-y-2.5">
            {filteredEmployees.map((item: any) => {
              const p = item.payroll;
              return (
                <div
                  key={item.displayName}
                  className="rounded-2xl border border-slate-200/80 bg-white/90 p-3.5 hover:border-[#1b6b44]/40 transition shadow-2xs"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    {/* Left: Employee info */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-sm text-slate-900 truncate">
                          {item.displayName}
                        </span>
                        <Badge variant="outline" className="text-[10px] py-0 font-bold border-slate-200 text-slate-600">
                          ฿{p.dailyRate}/วัน
                        </Badge>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 font-semibold mt-1">
                        <span>
                          ทำงาน: <strong className="font-numeric text-emerald-700">{p.completeDays} วัน</strong>
                        </span>
                        <span>
                          OT: <strong className="font-numeric text-amber-700">{p.otHours} ชม.</strong>
                        </span>
                        {p.lateDays > 0 && (
                          <span className="text-rose-600">
                            สาย: <strong className="font-numeric">{p.lateDays} วัน</strong>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Right: Financial breakdown */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs font-numeric">
                      <div className="text-right">
                        <span className="text-[10px] font-semibold text-slate-400 block">ค่าแรงฐาน</span>
                        <span className="font-bold text-slate-700">฿{p.basePay.toLocaleString()}</span>
                      </div>

                      <div className="text-right">
                        <span className="text-[10px] font-semibold text-slate-400 block">ค่า OT</span>
                        <span className="font-bold text-amber-700">
                          {p.otPay > 0 ? `+฿${p.otPay.toLocaleString()}` : "฿0"}
                        </span>
                      </div>

                      {p.otherAllowances > 0 && (
                        <div className="text-right">
                          <span className="text-[10px] font-semibold text-slate-400 block">ค่าอื่นๆ</span>
                          <span className="font-bold text-teal-700">+{p.otherAllowances.toLocaleString()}</span>
                        </div>
                      )}

                      {p.advanceDeduction > 0 && (
                        <div className="text-right">
                          <span className="text-[10px] font-semibold text-slate-400 block">หักเบิกล่วงหน้า</span>
                          <span className="font-black text-rose-700">
                            -฿{p.advanceDeduction.toLocaleString()}
                          </span>
                        </div>
                      )}

                      <div className="text-right pl-2 sm:pl-3 border-l border-slate-200">
                        <span className="text-[10px] font-black text-[#1b6b44] block">ยอดจ่ายสุทธิ</span>
                        <span className="text-base font-black text-[#134e31]">
                          ฿{p.netPay.toLocaleString()}
                        </span>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => openPayrollPdf({ periodLabel, startDate, endDate, employees: [item], title: `ใบรับเงินค่าแรงและ OT — ${item.displayName}` })}
                        className="rounded-xl border-emerald-200 text-emerald-800 text-xs font-bold"
                      >
                        <FileText size={13} className="mr-1" /> ใบรับเงิน PDF
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
